/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';

import { serverBaseWebSocketUrl } from '../../general-utilities/urlUtils';
import { MessageBase, MessageStatus, MessageType, TerrainMessage } from '../../models/Message';
import { campaignRootStore } from '../../stores/CampaignRootStore';
import sessionStore from '../../stores/SessionStore';

const processMessageData = async (message: MessageBase, t: TFunction<'Translation', undefined>): Promise<void> => {
  if (message.messageType === MessageType.CropInspectionNeeded) {
    const terrainMessage = message as TerrainMessage;
    try {
      const response = await fetch(terrainMessage.imageServerURL);
      if (response.ok) {
        const blob = await response.blob();
        const imageClientURL = URL.createObjectURL(blob);
        campaignRootStore.messageStore.addReceivedMessage({
          ...terrainMessage,
          imageClientURL
        });
      }
    } catch (error) {
      toast.error(t('general_errors.error_fetching_image'));
    }
  }
  else {
    campaignRootStore.messageStore.addReceivedMessage(message);
  }
}

export const MessageController = observer(({ orgId, campaignId }: { orgId: number, campaignId: string }) => {
  const { messageToBeSent, messagesToBeSent } = campaignRootStore.messageStore;
  const { t } = useTranslation();

  const connect = (): void => {
    const { socket } = campaignRootStore.messageStore;
  
    if (socket) {
      return;
    }

    const socketNew = new WebSocket(`${serverBaseWebSocketUrl}/ws/org/${orgId}/campaign/${campaignId}/messages/`);
    campaignRootStore.messageStore.updateSocket(socketNew);

    socketNew.onopen = () => {
      campaignRootStore.messageStore.updateIsConnected(true);
    };

    socketNew.onmessage = async (event) => {
      const messageDataFetched: MessageBase = JSON.parse(event.data);
      await processMessageData(messageDataFetched, t);
    };

    socketNew.onclose = () => {
      campaignRootStore.messageStore.updateIsConnected(false);
      campaignRootStore.messageStore.updateSocket(null);

      setTimeout(connect, 5000);
    };

    socketNew.onerror = () => {
      socketNew.close();
    };
  }

  const disconnect = (): void => {
    const { socket } = campaignRootStore.messageStore;

    if (socket) {
      socket.close();
    }

    Object.values(campaignRootStore.messageStore.receivedMessages).forEach(receivedMessage => {
      if (receivedMessage.messageType === MessageType.CropInspectionNeeded) {
        const terrainMessage = receivedMessage as TerrainMessage;
        if (terrainMessage?.imageClientURL) {
          URL.revokeObjectURL(terrainMessage.imageClientURL);
        }
      }
    });
    campaignRootStore.messageStore.clearMessages();
  }

  useEffect(() => {
    if (messageToBeSent && !messageToBeSent.clientMessageId) {
      const assignedClientMessageId = campaignRootStore.messageStore.assignNewClientId();
      const newMessageToBeSent = {
        ...messageToBeSent,
        clientMessageId: assignedClientMessageId.toString(),
        messageStatus: MessageStatus.Pending
      }
      campaignRootStore.messageStore.addOrUpdateMessageToBeSent(newMessageToBeSent);
      campaignRootStore.messageStore.setMessageToBeSent(null);
    }
  }, [messageToBeSent]);

  useEffect(() => {
    const { socket, isConnected } = campaignRootStore.messageStore;

    if (isConnected && socket) {
      Object.keys(messagesToBeSent)
        .filter(clientMessageId => messagesToBeSent[clientMessageId].messageStatus === MessageStatus.Pending)
        .forEach(clientMessageId => {
          let sent = false;
          try {
            const messageToSend: any = { // eslint-disable-line @typescript-eslint/no-explicit-any
              clientId: messagesToBeSent[clientMessageId].clientMessageId,
              user_id: sessionStore.user?.id, // TODO delete after backend is updated.
              ...messagesToBeSent[clientMessageId]
            }
            delete messageToSend[clientMessageId];
            socket.send(JSON.stringify(messageToSend));
            sent = true;
          }
          catch (error) {
            toast.error(t('general_errors.could_not_send_message'));
          }
          if (sent) {
            campaignRootStore.messageStore.addOrUpdateMessageToBeSent({
              ...messagesToBeSent[clientMessageId],
              messageStatus: MessageStatus.Confirmed
            });
          }
          else {
            campaignRootStore.messageStore.addOrUpdateMessageToBeSent({
              ...messagesToBeSent[clientMessageId],
              messageStatus: MessageStatus.Failed
            });
          }
        });
    }
  }, [messagesToBeSent]);

  useEffect(() => {
    connect();

    return () => disconnect();
  }, []);

  return null;
});
